// ═══════════════════════════════════════════════════════════════
// QuotaBanner.tsx – figyelmeztető sáv a szervezet kvótájáról
//
// A küszöb (alapból 80%) felett sárga, 100%-nál piros (új feltöltés / bejegyzés tiltva). A foglalt tárhelybe a
// Lomtár és az Archívum is beleszámít. Adminnak: „Archívum letöltése” és „Plusz tárhely igénylése” (a kérés
// hibajegyként a rendszergazdához kerül, aki egy lépésben jóváhagyja). A sáv munkamenetenként elrejthető,
// de szintváltáskor (figyelmeztetés → betelt) újra megjelenik.
// ═══════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useState } from 'react';
import { ApiClient, type QuotaStatus } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { ArchiveModal } from '../transparency/ArchiveModal';
import { AlertTriangle, HardDrive, Download, Send, X, RefreshCw, Clock } from 'lucide-react';

const mb = (b: number) => `${(b / 1048576).toFixed(b < 10 * 1048576 ? 1 : 0)} MB`;
const DISMISS_KEY = 'kozad_quota_banner_dismissed';

export const QuotaBanner: React.FC<{ refreshKey?: string | number }> = ({ refreshKey }) => {
  const { role, tenant } = useAuth();
  const isAdmin = ['superadmin', 'tenant_admin', 'org_admin'].includes(String(role));
  const [st, setSt] = useState<QuotaStatus | null>(null);
  const [dismissed, setDismissed] = useState<string>(() => { try { return sessionStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; } });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [addMb, setAddMb] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await ApiClient.getQuota().catch(() => null);
    if (r?.ok && r.data) setSt(r.data);
  }, []);
  useEffect(() => { load(); }, [load, refreshKey, tenant?.id]);
  useEffect(() => {
    const t = window.setInterval(load, 5 * 60 * 1000);
    const on = () => load();
    window.addEventListener('quota:changed', on);
    return () => { window.clearInterval(t); window.removeEventListener('quota:changed', on); };
  }, [load]);

  if (!st || st.level === 'ok') return null;
  const dismissId = `${st.tenant_id}:${st.level}`;
  if (dismissed === dismissId) return null;

  const full = st.level === 'full';
  const s = st.storage; const r = st.records;
  const pending = st.request?.status === 'pending' ? st.request : null;
  // az elutasítást 30 napig mutatjuk
  const rejected = st.request?.status === 'rejected' && st.request.decided_at && Date.now() - Date.parse(st.request.decided_at) < 30 * 86400000 ? st.request : null;
  const suggest = Math.max(100, Math.ceil(((s.limit_mb || 500) * 0.5) / 100) * 100);

  const send = async () => {
    const n = Number(addMb || suggest);
    if (!Number.isFinite(n) || n < 10) { setError('Legalább 10 MB adható meg'); return; }
    setBusy(true); setError(null);
    const res = await ApiClient.requestQuota(Math.round(n), msg.trim());
    setBusy(false);
    if (res.ok) { setFormOpen(false); setMsg(''); setAddMb(''); load(); } else setError(res.error || 'Az igény nem küldhető el');
  };
  const dismiss = () => { setDismissed(dismissId); try { sessionStorage.setItem(DISMISS_KEY, dismissId); } catch { /* */ } };

  const tone = full
    ? 'border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 text-rose-900 dark:text-rose-100'
    : 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100';

  return (
    <div role={full ? 'alert' : 'status'} className={`mb-4 rounded-xl border px-4 py-3 text-xs ${tone}`}>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
        <div className="flex items-start gap-2 flex-1 min-w-[16rem]">
          {full ? <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> : <HardDrive className="w-4 h-4 shrink-0 mt-0.5" />}
          <div className="space-y-0.5">
            <div className="font-bold text-sm">
              {full ? 'Elérte a kvótát – új fájl / bejegyzés nem tölthető fel' : `A kvóta ${st.warn_percent}%-a felett jár`}
            </div>
            {s.level !== 'ok' && (
              <div>
                Tárhely: <b>{mb(s.total)} / {s.limit_mb} MB ({s.percent}%)</b> – közzétett mellékletek {mb(s.attachments)}, Lomtár {mb(s.trash)}, Archívum {mb(s.archive)}.
              </div>
            )}
            {r.level !== 'ok' && <div>Bejegyzések: <b>{r.used} / {r.limit} ({r.percent}%)</b>.</div>}
            <div className="opacity-80">
              {isAdmin
                ? 'Töltse le az Archívumot (és a lejárt megőrzésű tételek törölhetők), ürítse a Lomtárat, vagy igényeljen plusz tárhelyet. Az archiválás a korlát elérésekor is működik.'
                : 'Kérjük, jelezze a szervezet adminisztrátorának (Archívum letöltése / plusz tárhely igénylése).'}
            </div>
            {pending && (
              <div className="flex items-center gap-1.5 font-semibold"><Clock className="w-3.5 h-3.5" />
                Tárhelybővítési igény elküldve ({new Date(pending.created_at).toLocaleDateString('hu-HU')}, +{pending.additional_mb} MB{pending.ticket_number ? `, ${pending.ticket_number}` : ''}) – elbírálásra vár.
              </div>
            )}
            {rejected && <div className="font-semibold">A legutóbbi igényt elutasították{rejected.reason ? `: ${rejected.reason}` : '.'}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <button type="button" onClick={() => setArchiveOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold bg-white/80 dark:bg-slate-900/60 border border-current/20 hover:bg-white dark:hover:bg-slate-900 cursor-pointer">
              <Download className="w-3.5 h-3.5" /> Archívum letöltése
            </button>
          )}
          {isAdmin && !pending && (
            <button type="button" onClick={() => setFormOpen((v) => !v)} aria-expanded={formOpen}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-white cursor-pointer ${full ? 'bg-rose-600 hover:bg-rose-500' : 'bg-amber-600 hover:bg-amber-500'}`}>
              <Send className="w-3.5 h-3.5" /> Plusz tárhely igénylése
            </button>
          )}
          {!full && (
            <button type="button" onClick={dismiss} aria-label="Elrejtés erre a munkamenetre" title="Elrejtés erre a munkamenetre" className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {formOpen && (
        <div className="mt-3 pt-3 border-t border-current/20 grid sm:grid-cols-[10rem_1fr_auto] gap-2 items-start">
          <label className="block">
            <span className="block font-semibold mb-1">Plusz tárhely (MB)</span>
            <input type="number" min={10} step={10} value={addMb} placeholder={String(suggest)} onChange={(e) => setAddMb(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-sm" />
          </label>
          <label className="block">
            <span className="block font-semibold mb-1">Indoklás (nem kötelező)</span>
            <input type="text" maxLength={2000} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="pl. a jogszabály szerint 5 évig megőrzendő pályázati anyagok"
              className="w-full px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-sm" />
          </label>
          <button type="button" onClick={send} disabled={busy}
            className="sm:mt-5 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-semibold bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer disabled:opacity-50">
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Igény elküldése
          </button>
          {error && <p role="alert" className="sm:col-span-3 text-rose-700 dark:text-rose-300">{error}</p>}
          <p className="sm:col-span-3 opacity-80">Az igény hibajegyként a rendszergazdához kerül; jóváhagyás után az új korlát azonnal érvényes.</p>
        </div>
      )}
      <ArchiveModal isOpen={archiveOpen} onClose={() => { setArchiveOpen(false); load(); }} />
    </div>
  );
};

export default QuotaBanner;
